/**
 * src/components/ReviewHistoryWithShare.tsx
 * 全文審閱歷史記錄組件（含分享功能）
 *
 * 分頁邏輯：
 * - 一般使用者：「我的記錄」+「分享給我的」
 * - 部門主管：多一個「部門紀錄」（同部門其他人的記錄）
 * - Admin / Super Admin：多一個「公司紀錄」（公司其他人的記錄）
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  List,
  Button,
  Tag,
  Space,
  Empty,
  message,
  Spin,
  Tabs,
  Tooltip,
  Popconfirm,
  Checkbox,
  Input,
} from 'antd';
import {
  HistoryOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  DownloadOutlined,
  BookOutlined,
  TeamOutlined,
  BankOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  getReviewHistory,
  deleteReviewHistory,
  batchDeleteReviewHistory,
  dismissSharing,
  batchDismissSharing,
  getReviewDetail,
  downloadOriginalFile,
  ReviewHistoryItem,
  ReviewHistoryDetail,
  ViewerRole,
  HistoryRecordType,
} from '../services/reviewHistoryService';
import ShareModal from './ShareModal';

interface ReviewHistoryWithShareProps {
  visible: boolean;
  onClose: () => void;
  onLoadHistory?: (detail: ReviewHistoryDetail) => void;
  recordType?: HistoryRecordType;
}

const ReviewHistoryWithShare: React.FC<ReviewHistoryWithShareProps> = ({
  visible,
  onClose,
  onLoadHistory,
  recordType = 'review',
}) => {
  const [loading, setLoading] = useState(false);
  const [ownRecords, setOwnRecords] = useState<ReviewHistoryItem[]>([]);
  const [departmentRecords, setDepartmentRecords] = useState<ReviewHistoryItem[]>([]);
  const [companyRecords, setCompanyRecords] = useState<ReviewHistoryItem[]>([]);
  const [sharedRecords, setSharedRecords] = useState<ReviewHistoryItem[]>([]);
  const [viewerRole, setViewerRole] = useState<ViewerRole>('user');
  const [activeTab, setActiveTab] = useState('own');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // 「部門紀錄」或「公司紀錄」tab 的選取
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<number>>(new Set());
  // 「分享給我的」tab 的選取（以 sharing_id 為鍵）
  const [selectedSharingIds, setSelectedSharingIds] = useState<Set<number>>(new Set());

  // 搜尋關鍵字
  const [searchKeyword, setSearchKeyword] = useState('');

  // 分享對話框狀態
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [currentShareRecord, setCurrentShareRecord] = useState<{
    id: number;
    title: string;
  } | null>(null);

  useEffect(() => {
    if (visible) {
      loadHistory();
    } else {
      setSelectedIds(new Set());
      setSelectedGroupIds(new Set());
      setSelectedSharingIds(new Set());
      setSearchKeyword('');
    }
  }, [visible]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await getReviewHistory(recordType);
      setOwnRecords(response.own_records);
      setDepartmentRecords(response.department_records || []);
      setCompanyRecords(response.company_records || []);
      setSharedRecords(response.shared_records);
      setViewerRole(response.viewer_role || 'user');
    } catch (error: any) {
      console.error('載入歷史記錄失敗:', error);
      if (error.response?.status === 401) {
        message.error('請先登入以使用歷史記錄功能');
      } else {
        message.error('載入歷史記錄失敗: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  // ===== 搜尋過濾 =====

  const filterRecords = (records: ReviewHistoryItem[]): ReviewHistoryItem[] => {
    if (!searchKeyword.trim()) return records;
    const keyword = searchKeyword.trim().toLowerCase();
    return records.filter(r => {
      const filename = (r.title || r.original_filename || '').toLowerCase();
      const owner = (r.owner_name || '').toLowerCase();
      const ruleName = (r.rule_set_name || '').toLowerCase();
      const sharer = (r.sharer_name || '').toLowerCase();
      return (
        filename.includes(keyword) ||
        owner.includes(keyword) ||
        ruleName.includes(keyword) ||
        sharer.includes(keyword)
      );
    });
  };

  const filteredOwn = useMemo(() => filterRecords(ownRecords), [ownRecords, searchKeyword]);
  const filteredDepartment = useMemo(() => filterRecords(departmentRecords), [departmentRecords, searchKeyword]);
  const filteredCompany = useMemo(() => filterRecords(companyRecords), [companyRecords, searchKeyword]);
  const filteredShared = useMemo(() => filterRecords(sharedRecords), [sharedRecords, searchKeyword]);

  const handleDelete = async (recordId: number) => {
    try {
      await deleteReviewHistory(recordId);
      message.success('刪除成功');
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(recordId);
        return next;
      });
      loadHistory();
    } catch (error: any) {
      console.error('刪除失敗:', error);
      message.error('刪除失敗: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleView = async (recordId: number) => {
    try {
      const detail = await getReviewDetail(recordId);
      if (onLoadHistory) {
        onLoadHistory(detail);
        onClose();
      }
    } catch (error: any) {
      console.error('載入記錄詳情失敗:', error);
      message.error('載入記錄詳情失敗: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredOwn.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredOwn.map(r => r.id)));
    }
  };

  const handleBatchDelete = () => {
    Modal.confirm({
      title: `確定要刪除 ${selectedIds.size} 筆記錄嗎？`,
      content: '此操作無法復原。',
      okText: '確定刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await batchDeleteReviewHistory(Array.from(selectedIds));
          message.success(`成功刪除 ${result.deleted_count} 筆記錄`);
          if (result.failed_ids.length > 0) {
            message.warning(`${result.failed_ids.length} 筆記錄無法刪除`);
          }
          setSelectedIds(new Set());
          loadHistory();
        } catch (error: any) {
          message.error('批次刪除失敗: ' + (error.response?.data?.detail || error.message));
        }
      },
    });
  };

  // ===== 部門紀錄 / 公司紀錄 tab 選取 =====

  const activeGroupRecords =
    activeTab === 'department' ? filteredDepartment : filteredCompany;

  const handleToggleGroupSelect = (id: number) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAllGroup = () => {
    if (selectedGroupIds.size === activeGroupRecords.length) {
      setSelectedGroupIds(new Set());
    } else {
      setSelectedGroupIds(new Set(activeGroupRecords.map(r => r.id)));
    }
  };

  const handleBatchDeleteGroup = () => {
    Modal.confirm({
      title: `確定要刪除 ${selectedGroupIds.size} 筆記錄嗎？`,
      content: '此操作無法復原。',
      okText: '確定刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await batchDeleteReviewHistory(Array.from(selectedGroupIds));
          message.success(`成功刪除 ${result.deleted_count} 筆記錄`);
          if (result.failed_ids.length > 0) {
            message.warning(`${result.failed_ids.length} 筆記錄無法刪除`);
          }
          setSelectedGroupIds(new Set());
          loadHistory();
        } catch (error: any) {
          message.error('批次刪除失敗: ' + (error.response?.data?.detail || error.message));
        }
      },
    });
  };

  const handleToggleSharedSelect = (sharingId: number) => {
    setSelectedSharingIds(prev => {
      const next = new Set(prev);
      if (next.has(sharingId)) next.delete(sharingId);
      else next.add(sharingId);
      return next;
    });
  };

  const handleSelectAllShared = () => {
    const allIds = filteredShared.map(r => r.sharing_id!).filter(Boolean);
    if (selectedSharingIds.size === allIds.length) {
      setSelectedSharingIds(new Set());
    } else {
      setSelectedSharingIds(new Set(allIds));
    }
  };

  const handleDismiss = async (sharingId: number) => {
    try {
      await dismissSharing(sharingId);
      message.success('已移除');
      setSelectedSharingIds(prev => {
        const next = new Set(prev);
        next.delete(sharingId);
        return next;
      });
      loadHistory();
    } catch (error: any) {
      message.error('移除失敗: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBatchDismiss = () => {
    Modal.confirm({
      title: `確定要移除 ${selectedSharingIds.size} 筆分享記錄嗎？`,
      content: '移除後你將看不到這些分享，但原始記錄不受影響。',
      okText: '確定移除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await batchDismissSharing(Array.from(selectedSharingIds));
          message.success(`成功移除 ${result.dismissed_count} 筆分享`);
          if (result.failed_ids.length > 0) {
            message.warning(`${result.failed_ids.length} 筆無法移除`);
          }
          setSelectedSharingIds(new Set());
          loadHistory();
        } catch (error: any) {
          message.error('批次移除失敗: ' + (error.response?.data?.detail || error.message));
        }
      },
    });
  };

  const handleShare = (record: ReviewHistoryItem) => {
    setCurrentShareRecord({
      id: record.id,
      title: record.title || record.original_filename,
    });
    setShareModalVisible(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Taipei',
    });
  };

  const handleDownload = async (record: ReviewHistoryItem) => {
    try {
      await downloadOriginalFile(record.id, record.original_filename);
      message.success('檔案下載成功');
    } catch (error: any) {
      console.error('下載檔案失敗:', error);
      if (error.response?.status === 404) {
        message.error('此記錄沒有原始檔案');
      } else {
        message.error('下載失敗: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  // ===== 渲染記錄項目 =====

  /** 我的記錄（可刪除、分享） */
  const renderOwnRecordItem = (record: ReviewHistoryItem) => (
    <List.Item
      key={record.id}
      style={{ cursor: 'pointer' }}
      onClick={() => handleView(record.id)}
      actions={[
        <Tooltip key='view' title='查看詳情'>
          <Button
            type='text'
            icon={<EyeOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleView(record.id);
            }}
          />
        </Tooltip>,
        ...(record.has_file_data
          ? [
              <Tooltip key='download' title='下載原始檔案'>
                <Button
                  type='text'
                  icon={<DownloadOutlined />}
                  onClick={e => {
                    e.stopPropagation();
                    handleDownload(record);
                  }}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>,
            ]
          : []),
        <Tooltip key='share' title='分享給同事'>
          <Button
            type='text'
            icon={<ShareAltOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleShare(record);
            }}
          />
        </Tooltip>,
        <Popconfirm
          key='delete'
          title='確定要刪除這筆記錄嗎？'
          onConfirm={e => {
            e?.stopPropagation();
            handleDelete(record.id);
          }}
          onCancel={e => e?.stopPropagation()}
          okText='確定'
          cancelText='取消'
        >
          <Tooltip title='刪除記錄'>
            <Button
              type='text'
              danger
              icon={<DeleteOutlined />}
              onClick={e => e.stopPropagation()}
            />
          </Tooltip>
        </Popconfirm>,
      ]}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <Checkbox
          checked={selectedIds.has(record.id)}
          onChange={() => handleToggleSelect(record.id)}
          onClick={e => e.stopPropagation()}
          style={{ marginRight: 12, marginTop: 4, flexShrink: 0 }}
        />
        <List.Item.Meta
          avatar={<FileTextOutlined style={{ fontSize: 24, color: '#1E88A8' }} />}
          title={
            <Space>
              <span>{record.title || record.original_filename}</span>
              {record.is_shared && (
                <Tag icon={<ShareAltOutlined />} color='blue'>
                  分享
                </Tag>
              )}
            </Space>
          }
          description={
            <Space direction='vertical' size={4} style={{ width: '100%' }}>
              {record.owner_name && (
                <div style={{ color: '#595959' }}>
                  <UserOutlined style={{ marginRight: 4 }} />
                  <strong>{record.owner_name}</strong>
                  {record.owner_department && ` · ${record.owner_department}`}
                </div>
              )}
              <div>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {formatDate(record.created_at)}
              </div>
              {record.is_shared && record.sharer_name && (
                <div style={{ color: '#1E88A8' }}>
                  <ShareAltOutlined style={{ marginRight: 4 }} />由 {record.sharer_name} 分享
                  {record.share_message && ` · ${record.share_message}`}
                </div>
              )}
              <Space>
                <Tag>總計 {record.total_issues} 個建議</Tag>
                {record.rule_set_name && (
                  <Tag icon={<BookOutlined />} color='geekblue'>
                    {record.rule_set_name}
                  </Tag>
                )}
              </Space>
            </Space>
          }
        />
      </div>
    </List.Item>
  );

  /** 部門紀錄 / 公司紀錄（可查看、下載、分享、刪除、勾選） */
  const renderGroupRecordItem = (record: ReviewHistoryItem) => (
    <List.Item
      key={record.id}
      style={{ cursor: 'pointer' }}
      onClick={() => handleView(record.id)}
      actions={[
        <Tooltip key='view' title='查看詳情'>
          <Button
            type='text'
            icon={<EyeOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleView(record.id);
            }}
          />
        </Tooltip>,
        ...(record.has_file_data
          ? [
              <Tooltip key='download' title='下載原始檔案'>
                <Button
                  type='text'
                  icon={<DownloadOutlined />}
                  onClick={e => {
                    e.stopPropagation();
                    handleDownload(record);
                  }}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>,
            ]
          : []),
        <Tooltip key='share' title='分享給同事'>
          <Button
            type='text'
            icon={<ShareAltOutlined />}
            onClick={e => {
              e.stopPropagation();
              handleShare(record);
            }}
          />
        </Tooltip>,
        <Popconfirm
          key='delete'
          title='確定要刪除這筆記錄嗎？'
          onConfirm={e => {
            e?.stopPropagation();
            handleDelete(record.id);
          }}
          onCancel={e => e?.stopPropagation()}
          okText='確定'
          cancelText='取消'
        >
          <Tooltip title='刪除記錄'>
            <Button
              type='text'
              danger
              icon={<DeleteOutlined />}
              onClick={e => e.stopPropagation()}
            />
          </Tooltip>
        </Popconfirm>,
      ]}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
        <Checkbox
          checked={selectedGroupIds.has(record.id)}
          onChange={() => handleToggleGroupSelect(record.id)}
          onClick={e => e.stopPropagation()}
          style={{ marginRight: 12, marginTop: 4, flexShrink: 0 }}
        />
        <List.Item.Meta
          avatar={<FileTextOutlined style={{ fontSize: 24, color: '#1E88A8' }} />}
          title={
            <span>{record.title || record.original_filename}</span>
          }
          description={
            <Space direction='vertical' size={4} style={{ width: '100%' }}>
              {record.owner_name && (
                <div style={{ color: '#595959' }}>
                  <UserOutlined style={{ marginRight: 4 }} />
                  <strong>{record.owner_name}</strong>
                  {record.owner_department && ` · ${record.owner_department}`}
                </div>
              )}
              <div>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {formatDate(record.created_at)}
              </div>
              <Space>
                <Tag>總計 {record.total_issues} 個建議</Tag>
                {record.rule_set_name && (
                  <Tag icon={<BookOutlined />} color='geekblue'>
                    {record.rule_set_name}
                  </Tag>
                )}
              </Space>
            </Space>
          }
        />
      </div>
    </List.Item>
  );

  /** 分享給我的（可移除分享） */
  const renderSharedRecordItem = (record: ReviewHistoryItem) => {
    const sharingId = record.sharing_id!;
    return (
      <List.Item
        key={record.id}
        style={{ cursor: 'pointer' }}
        onClick={() => handleView(record.id)}
        actions={[
          <Tooltip key='view' title='查看詳情'>
            <Button
              type='text'
              icon={<EyeOutlined />}
              onClick={e => {
                e.stopPropagation();
                handleView(record.id);
              }}
            />
          </Tooltip>,
          ...(record.has_file_data
            ? [
                <Tooltip key='download' title='下載原始檔案'>
                  <Button
                    type='text'
                    icon={<DownloadOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      handleDownload(record);
                    }}
                    style={{ color: '#52c41a' }}
                  />
                </Tooltip>,
              ]
            : []),
          <Popconfirm
            key='dismiss'
            title='確定要移除此分享嗎？'
            description='移除後你將看不到此記錄，原始記錄不受影響。'
            onConfirm={e => {
              e?.stopPropagation();
              handleDismiss(sharingId);
            }}
            onCancel={e => e?.stopPropagation()}
            okText='確定移除'
            cancelText='取消'
          >
            <Tooltip title='移除分享'>
              <Button
                type='text'
                danger
                icon={<DeleteOutlined />}
                onClick={e => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>,
        ]}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <Checkbox
            checked={selectedSharingIds.has(sharingId)}
            onChange={() => handleToggleSharedSelect(sharingId)}
            onClick={e => e.stopPropagation()}
            style={{ marginRight: 12, marginTop: 4, flexShrink: 0 }}
          />
          <List.Item.Meta
            avatar={<FileTextOutlined style={{ fontSize: 24, color: '#1E88A8' }} />}
            title={
              <Space>
                <span>{record.title || record.original_filename}</span>
                <Tag icon={<ShareAltOutlined />} color='blue'>
                  分享
                </Tag>
              </Space>
            }
            description={
              <Space direction='vertical' size={4} style={{ width: '100%' }}>
                {record.owner_name && (
                  <div style={{ color: '#1E88A8' }}>
                    <UserOutlined style={{ marginRight: 4 }} />
                    <strong>{record.owner_name}</strong>
                    {record.owner_department && ` · ${record.owner_department}`}
                  </div>
                )}
                <div>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                  {formatDate(record.created_at)}
                </div>
                {record.share_message && (
                  <div style={{ color: '#8c8c8c', fontSize: 12 }}>{record.share_message}</div>
                )}
                <Space>
                  <Tag>總計 {record.total_issues} 個建議</Tag>
                  {record.rule_set_name && (
                    <Tag icon={<BookOutlined />} color='geekblue'>
                      {record.rule_set_name}
                    </Tag>
                  )}
                </Space>
              </Space>
            }
          />
        </div>
      </List.Item>
    );
  };

  // ===== 組裝 Tab 項目 =====

  const getTabBarExtraContent = () => {
    if (activeTab === 'own' && filteredOwn.length > 0) {
      return {
        right: (
          <Space size={8}>
            <Checkbox
              indeterminate={selectedIds.size > 0 && selectedIds.size < filteredOwn.length}
              checked={selectedIds.size === filteredOwn.length && filteredOwn.length > 0}
              onChange={handleSelectAll}
            >
              全選
            </Checkbox>
            <Button
              type='primary'
              danger
              size='small'
              icon={<DeleteOutlined />}
              disabled={selectedIds.size === 0}
              onClick={handleBatchDelete}
            >
              刪除{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
            </Button>
          </Space>
        ),
      };
    }
    if ((activeTab === 'department' || activeTab === 'company') && activeGroupRecords.length > 0) {
      return {
        right: (
          <Space size={8}>
            <Checkbox
              indeterminate={
                selectedGroupIds.size > 0 && selectedGroupIds.size < activeGroupRecords.length
              }
              checked={
                selectedGroupIds.size === activeGroupRecords.length &&
                activeGroupRecords.length > 0
              }
              onChange={handleSelectAllGroup}
            >
              全選
            </Checkbox>
            <Button
              type='primary'
              danger
              size='small'
              icon={<DeleteOutlined />}
              disabled={selectedGroupIds.size === 0}
              onClick={handleBatchDeleteGroup}
            >
              刪除{selectedGroupIds.size > 0 ? ` (${selectedGroupIds.size})` : ''}
            </Button>
          </Space>
        ),
      };
    }
    if (activeTab === 'shared' && filteredShared.length > 0) {
      return {
        right: (
          <Space size={8}>
            <Checkbox
              indeterminate={
                selectedSharingIds.size > 0 && selectedSharingIds.size < filteredShared.length
              }
              checked={
                selectedSharingIds.size === filteredShared.length && filteredShared.length > 0
              }
              onChange={handleSelectAllShared}
            >
              全選
            </Checkbox>
            <Button
              type='primary'
              danger
              size='small'
              icon={<DeleteOutlined />}
              disabled={selectedSharingIds.size === 0}
              onClick={handleBatchDismiss}
            >
              移除{selectedSharingIds.size > 0 ? ` (${selectedSharingIds.size})` : ''}
            </Button>
          </Space>
        ),
      };
    }
    return undefined;
  };

  const buildTabItems = () => {
    const items: Array<{
      key: string;
      label: React.ReactNode;
      children: React.ReactNode;
    }> = [
      {
        key: 'own',
        label: (
          <span>
            <FileTextOutlined />
            我的記錄 ({filteredOwn.length})
          </span>
        ),
        children: (
          <div>
            {filteredOwn.length === 0 ? (
              <Empty
                description={searchKeyword.trim() ? '沒有符合搜尋條件的記錄' : '暫無歷史記錄'}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List dataSource={filteredOwn} renderItem={record => renderOwnRecordItem(record)} />
            )}
          </div>
        ),
      },
    ];

    // 部門主管：「部門紀錄」分頁
    if (viewerRole === 'department_manager') {
      items.push({
        key: 'department',
        label: (
          <span>
            <TeamOutlined />
            部門紀錄 ({filteredDepartment.length})
          </span>
        ),
        children: (
          <div>
            {filteredDepartment.length === 0 ? (
              <Empty
                description={searchKeyword.trim() ? '沒有符合搜尋條件的記錄' : '暫無部門紀錄'}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                dataSource={filteredDepartment}
                renderItem={record => renderGroupRecordItem(record)}
              />
            )}
          </div>
        ),
      });
    }

    // Admin / Super Admin：「公司紀錄」分頁
    if (viewerRole === 'admin') {
      items.push({
        key: 'company',
        label: (
          <span>
            <BankOutlined />
            公司紀錄 ({filteredCompany.length})
          </span>
        ),
        children: (
          <div>
            {filteredCompany.length === 0 ? (
              <Empty
                description={searchKeyword.trim() ? '沒有符合搜尋條件的記錄' : '暫無公司紀錄'}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <List
                dataSource={filteredCompany}
                renderItem={record => renderGroupRecordItem(record)}
              />
            )}
          </div>
        ),
      });
    }

    // 分享給我的
    items.push({
      key: 'shared',
      label: (
        <span>
          <ShareAltOutlined />
          分享給我的 ({filteredShared.length})
        </span>
      ),
      children: (
        <div>
          {filteredShared.length === 0 ? (
            <Empty
              description={searchKeyword.trim() ? '沒有符合搜尋條件的記錄' : '暫無分享記錄'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <List
              dataSource={filteredShared}
              renderItem={record => renderSharedRecordItem(record)}
            />
          )}
        </div>
      ),
    });

    return items;
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>{recordType === 'comparison' ? '比對歷史記錄' : '審閱歷史記錄'}</span>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={800}
        footer={null}
        destroyOnClose
        style={{ top: 24 }}
        styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
      >
        <Spin spinning={loading}>
          <Input
            placeholder='搜尋檔名、使用者、規則名稱...'
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
            value={searchKeyword}
            onChange={e => {
              setSearchKeyword(e.target.value);
              setSelectedIds(new Set());
              setSelectedGroupIds(new Set());
              setSelectedSharingIds(new Set());
            }}
            style={{ marginBottom: 12 }}
          />
          <Tabs
            activeKey={activeTab}
            onChange={key => {
              setActiveTab(key);
              setSelectedIds(new Set());
              setSelectedGroupIds(new Set());
              setSelectedSharingIds(new Set());
            }}
            tabBarExtraContent={getTabBarExtraContent()}
            items={buildTabItems()}
          />
        </Spin>
      </Modal>

      {/* 分享對話框 */}
      {currentShareRecord && (
        <ShareModal
          visible={shareModalVisible}
          onClose={() => {
            setShareModalVisible(false);
            setCurrentShareRecord(null);
          }}
          recordType='review'
          recordId={currentShareRecord.id}
          recordTitle={currentShareRecord.title}
        />
      )}
    </>
  );
};

export default ReviewHistoryWithShare;
